import React, { useState, useRef, useEffect } from "react";
import {
  Popper,
  Box,
  Button,
  Typography,
  useMediaQuery,
  Dialog,
  DialogContent,
  DialogActions,
  Tooltip,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers-pro/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import { DateRangeCalendar } from "@mui/x-date-pickers-pro/DateRangeCalendar";
import dayjs from "dayjs";
import { useTheme } from "@mui/material/styles";
import { LicenseInfo } from "@mui/x-license";
import calender from ".././Assets/images/calendar.gif";

const CustomDateRangeCalendar = ({ value, onChange }) => {
  LicenseInfo.setLicenseKey(
    "e0d9bb8070ce0054c9d9ecb6e82cb58fTz0wLEU9MzI0NzIxNDQwMDAwMDAsUz1wcmVtaXVtLExNPXBlcnBldHVhbCxLVj0y",
  );

  const [open, setOpen] = useState(false);
  const [tempValue, setTempValue] = useState(value || [null, null]);
  const anchorRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Sync internal state when prop value changes (important for reset)
  useEffect(() => {
    setTempValue(value || [null, null]);
  }, [value]);

  const handleApply = () => {
    onChange(tempValue);
    setOpen(false);
  };

  const handleCancel = () => {
    setTempValue(value || [null, null]); // Revert to actual selected value
    setOpen(false);
  };

  const handleClear = () => {
    setTempValue([null, null]);
  };

  // Format the date for the display
  const formatDateDisplay = () => {
    if (value?.[0] && value?.[1]) {
      return `${dayjs(value[0]).format("DD MMM YYYY")} - ${dayjs(value[1]).format("DD MMM YYYY")}`;
    }
    return "No Date Selected";
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box display="flex" alignItems="center" gap={1}>
        {/* ✅ Read-only Date Display (Disabled look) */}
        <Box
          sx={{
            padding: "4px 12px",
            backgroundColor: "#f9f9f9", // Slightly lighter for a cleaner look
            border: "1px solid #e0e0e0",
            borderRadius: "6px", // Slightly more rounded
            color: "text.secondary", // MUI standard secondary color
            fontSize: "0.875rem",
            width: "220px", // Fixed width to prevent layout jumping
            userSelect: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center", // Center text horizontally
            height: "36px", // standard input height
            boxShadow: "inset 0 1px 2px rgba(0,0,0,0.05)", // Subtle inset for "disabled field" feel
            overflow: "hidden", // Prevent text from leaking out
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {formatDateDisplay()}
          </Typography>
        </Box>

        {/* Calendar Icon */}
        <Box
          component="img"
          src={calender}
          alt="calendar"
          sx={{
            width: 28,
            height: 28,
            cursor: "pointer",
            ml: 1,
          }}
          ref={anchorRef}
          onClick={() => setOpen((prev) => !prev)}
        />

        {/* ✅ Reset Button */}
        <Tooltip title="Reset Date Range" arrow>
          <Box
            onClick={() => {
              setTempValue([null, null]);
              onChange([null, null]);
              setOpen(false);
            }}
            sx={{
              cursor: "pointer",
              ml: 1,
              fontSize: "20px",
              userSelect: "none",
              transition: "transform 0.2s ease-in-out",
              "&:active": {
                transform: "scale(1.2)",
              },
            }}
          >
            🔄
          </Box>
        </Tooltip>

        {/* Popper / Dialog Logic */}
        {isMobile ? (
          <Dialog open={open} onClose={handleCancel} fullWidth>
            <DialogContent>
              <DateRangeCalendar
                value={tempValue}
                onChange={(newRange) => setTempValue(newRange)}
              />
            </DialogContent>
            <DialogActions>
              <Button color="error" onClick={handleCancel}>
                Cancel
              </Button>
              <Button onClick={handleClear}>Clear</Button>
              <Button onClick={handleApply} variant="contained">
                Apply
              </Button>
            </DialogActions>
          </Dialog>
        ) : (
          <Popper
            open={open}
            anchorEl={anchorRef.current}
            placement="bottom-start"
            sx={{ zIndex: 1500 }}
          >
            <Box
              sx={{
                bgcolor: "background.paper",
                border: 1,
                borderColor: "divider",
                p: 2,
                boxShadow: 6,
                borderRadius: 1,
              }}
            >
              <DateRangeCalendar
                value={tempValue}
                onChange={(newRange) => setTempValue(newRange)}
              />
              <Box display="flex" justifyContent="flex-end" gap={1} mt={2}>
                <Button size="small" onClick={handleCancel} color="error">
                  Cancel
                </Button>
                <Button size="small" onClick={handleClear}>
                  Clear
                </Button>
                <Button size="small" variant="contained" onClick={handleApply}>
                  Apply
                </Button>
              </Box>
            </Box>
          </Popper>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default CustomDateRangeCalendar;
